/* HistorialUnidadPage.jsx — Historial público de taller de una unidad.
   URL: /historial-unidad/:id (sin login, compartible por link/QR).
   Octavio 20-ago-2026 · mejorado 24-ago-2026. */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* ── Helpers ── */
const MESES = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];

function fmtFecha(iso) {
  if (!iso) return "—";
  // Parsea YYYY-MM-DD sin convertir zona horaria
  const [y, m, d] = (iso.slice(0, 10)).split("-").map(Number);
  return `${d} ${MESES[(m || 1) - 1]} ${y}`;
}

function calcularDiasAnio(items) {
  const anio = new Date().getFullYear();
  let dias = 0, visitas = 0;
  const hoy = new Date();
  items.forEach((h) => {
    const entrada = new Date(h.fecha_entrada);
    if (isNaN(entrada) || entrada.getFullYear() !== anio) return;
    visitas++;
    const salida = h.fecha_regreso_real ? new Date(h.fecha_regreso_real) : hoy;
    const diff = Math.max(1, Math.round((salida - entrada) / 86_400_000));
    dias += diff;
  });
  return { dias, visitas };
}

/* ── Miniatura con botón de descarga individual ── */
function FotoThumb({ url, fotos, titulo, onOpen }) {
  const ext = url.startsWith("data:image/png") ? "png" : "jpg";
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => onOpen({ url, fotos, titulo })}
        style={{ border: "none", background: "none", padding: 0, cursor: "pointer", display: "block" }}
      >
        <img
          src={url}
          alt="foto"
          style={{
            width: 72, height: 72, objectFit: "cover",
            borderRadius: 8, border: "2px solid #E2E8F0",
            display: "block",
            transition: "transform 0.15s",
          }}
          onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.06)")}
          onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
        />
      </button>
      {/* Botón de descarga superpuesto — solo en pantalla, no en PDF */}
      <a
        href={url}
        download={`foto.${ext}`}
        className="no-print"
        title="Descargar foto"
        style={{
          position: "absolute", bottom: 2, right: 2,
          background: "rgba(0,0,0,0.65)", color: "white",
          borderRadius: 4, width: 20, height: 20,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 10, textDecoration: "none",
          cursor: "pointer",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <i className="fa-solid fa-download"/>
      </a>
    </div>
  );
}

function fmtTimestamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      timeZone: "America/Chihuahua",
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso.slice(0, 16).replace("T", " "); }
}

/* ── Componente principal ── */
export default function HistorialUnidadPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [fotoModal, setFotoModal] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/historial-unidad/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "No se encontró la unidad"));
  }, [id]);

  const { dias: diasTaller, visitas } = useMemo(
    () => (data ? calcularDiasAnio(data.items) : { dias: 0, visitas: 0 }),
    [data]
  );

  if (error) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
                  background: "#F8FAFC", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ textAlign: "center", padding: 32 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔧</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#0F172A", marginBottom: 6 }}>Unidad no encontrada</div>
        <div style={{ color: "#64748B", fontSize: 14 }}>{error}</div>
      </div>
    </div>
  );

  if (!data) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
                  background: "#F8FAFC" }}>
      <div style={{ color: "#64748B", fontSize: 14, fontFamily: "system-ui, sans-serif" }}>Cargando…</div>
    </div>
  );

  const enTaller = data.estado_actual === "EN_TALLER";
  const tipoLabel = data.tipo_vehiculo || "Pipa";
  const tieneDatosVehiculo = data.marca || data.modelo || data.departamento || data.resguardante;

  return (
    <>
      {/* ── Estilos de impresión ── */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { font-family: system-ui, sans-serif; background: white !important; }
          header { background: #0F172A !important; color: white !important;
                   -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .foto-thumb { width: 52px !important; height: 52px !important; }
        }
        @media screen {
          .print-only { display: none !important; }
        }
      `}</style>

      <div id="historial-print-root"
           style={{ minHeight: "100vh", background: "#F1F5F9", fontFamily: "system-ui, -apple-system, sans-serif" }}>

        {/* ── Header ── */}
        <header style={{
          background: "#0F172A", color: "white",
          padding: "20px 24px",
          display: "flex", flexDirection: "column", gap: 4,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
                        textTransform: "uppercase", color: "#94A3B8" }}>
            DMU · Chihuahua
          </div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>
            {tipoLabel} #{data.pipa_numero} — Historial de taller
          </div>
          <div style={{ marginTop: 6 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "3px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700,
              background: enTaller ? "#FEF3C7" : "#DCFCE7",
              color: enTaller ? "#92400E" : "#15803D",
            }}>
              {enTaller
                ? <><i className="fa-solid fa-screwdriver-wrench"/> EN TALLER</>
                : <><i className="fa-solid fa-circle-check"/> OPERATIVA</>}
            </span>
          </div>
        </header>

        {/* ── Contenido ── */}
        <main style={{ maxWidth: 640, margin: "0 auto", padding: "20px 16px" }}>

          {/* ── Datos básicos de la unidad ── */}
          {tieneDatosVehiculo && (
            <div style={{
              background: "white", borderRadius: 12, padding: "14px 16px",
              marginBottom: 14, border: "1px solid #E2E8F0",
              boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                            letterSpacing: "0.08em", color: "#94A3B8", marginBottom: 10 }}>
                Datos de la unidad
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
                {(data.marca || data.modelo) && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <span style={{ fontSize: 11, color: "#94A3B8" }}>Marca / Modelo</span>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>
                      {[data.marca, data.modelo].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                )}
                {data.descripcion && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <span style={{ fontSize: 11, color: "#94A3B8" }}>Descripción</span>
                    <div style={{ fontSize: 13, color: "#334155" }}>{data.descripcion}</div>
                  </div>
                )}
                {data.departamento && (
                  <div>
                    <span style={{ fontSize: 11, color: "#94A3B8" }}>Departamento</span>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{data.departamento}</div>
                  </div>
                )}
                {data.resguardante && (
                  <div>
                    <span style={{ fontSize: 11, color: "#94A3B8" }}>Resguardante</span>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{data.resguardante}</div>
                  </div>
                )}
              </div>
              {/* Días en taller este año */}
              {visitas > 0 && (
                <div style={{
                  marginTop: 12, padding: "8px 12px",
                  background: "#FEF3C7", borderRadius: 8,
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  <i className="fa-solid fa-calendar-days" style={{ color: "#92400E" }}/>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#92400E" }}>
                    {diasTaller} día{diasTaller !== 1 ? "s" : ""} en taller · {visitas} visita{visitas !== 1 ? "s" : ""} en {new Date().getFullYear()}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Días en taller (cuando no hay datos de vehículo pero sí historial) */}
          {!tieneDatosVehiculo && visitas > 0 && (
            <div style={{
              background: "#FEF3C7", borderRadius: 10, padding: "10px 16px",
              marginBottom: 14, display: "flex", alignItems: "center", gap: 8,
            }}>
              <i className="fa-solid fa-calendar-days" style={{ color: "#92400E" }}/>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#92400E" }}>
                {diasTaller} día{diasTaller !== 1 ? "s" : ""} en taller · {visitas} visita{visitas !== 1 ? "s" : ""} en {new Date().getFullYear()}
              </span>
            </div>
          )}

          {/* ── Botón PDF ── */}
          {data.total > 0 && (
            <div className="no-print" style={{ marginBottom: 14, display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => window.print()}
                style={{
                  background: "#1D4ED8", color: "white", border: "none",
                  padding: "8px 16px", borderRadius: 8, fontWeight: 700,
                  fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                }}
              >
                <i className="fa-solid fa-file-pdf"/>Descargar historial en PDF
              </button>
            </div>
          )}

          {/* ── Avisos del chofer (vía bot WhatsApp) ── */}
          {(data.avisos_chofer || []).length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                            letterSpacing: "0.08em", color: "#92400E", marginBottom: 10,
                            display: "flex", alignItems: "center", gap: 6 }}>
                <i className="fa-brands fa-whatsapp" style={{ color: "#25D366", fontSize: 14 }}/>
                Avisos del chofer (WhatsApp)
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {(data.avisos_chofer || []).map((a) => (
                  <div key={a.id} style={{
                    background: "#FFFBEB",
                    border: "1.5px solid #FDE68A",
                    borderLeft: "4px solid #F59E0B",
                    borderRadius: 10, padding: "12px 14px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <i className="fa-brands fa-whatsapp" style={{ color: "#25D366", fontSize: 13 }}/>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#92400E",
                                    textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Aviso del chofer
                      </span>
                      <span style={{ marginLeft: "auto", fontSize: 11, color: "#B45309" }}>
                        {fmtTimestamp(a.timestamp)}
                      </span>
                    </div>
                    <div style={{ fontSize: 14, color: "#1C1917", marginBottom: 6 }}>
                      {a.motivo_corregido || a.motivo_original}
                    </div>
                    {a.chofer_nombre && (
                      <div style={{ fontSize: 11, color: "#78716C", display: "flex",
                                    alignItems: "center", gap: 4 }}>
                        <i className="fa-solid fa-user"/>
                        Chofer: {a.chofer_nombre}
                      </div>
                    )}
                    {a.regreso_servicio ? (
                      <div style={{ marginTop: 8, fontSize: 11, fontWeight: 700,
                                    color: "#15803D", display: "flex", alignItems: "center", gap: 5,
                                    background: "#DCFCE7", borderRadius: 6, padding: "4px 8px",
                                    display: "inline-flex" }}>
                        <i className="fa-solid fa-circle-check"/>
                        De vuelta en servicio: {fmtTimestamp(a.regreso_servicio)}
                      </div>
                    ) : (
                      <div style={{ marginTop: 8, fontSize: 11, color: "#B45309",
                                    display: "inline-flex", alignItems: "center", gap: 5,
                                    background: "#FEF9C3", borderRadius: 6, padding: "4px 8px" }}>
                        <i className="fa-solid fa-clock"/>
                        Sin regreso a servicio registrado aún
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Lista de registros oficiales ── */}
          {data.total === 0 && (data.avisos_chofer || []).length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, background: "white", borderRadius: 12,
                          color: "#94A3B8", fontSize: 14 }}>
              Sin registros de taller para esta unidad.
            </div>
          ) : data.total === 0 ? null : (
            <>
            {/* Sección encabezado registros oficiales cuando hay avisos también */}
            {(data.avisos_chofer || []).length > 0 && (
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                            letterSpacing: "0.08em", color: "#475569", marginBottom: 10,
                            display: "flex", alignItems: "center", gap: 6 }}>
                <i className="fa-solid fa-screwdriver-wrench" style={{ color: "#475569" }}/>
                Registros oficiales de taller
              </div>
            )}
            <div style={{ display: "grid", gap: 12 }}>
              {data.items.map((h) => {
                const fotosE = h.fotos_entrada || [];
                const fotosS = h.fotos_salida || [];
                const abierto = !h.fecha_regreso_real;
                const tituloE = `${tipoLabel} #${h.pipa_numero} — Entrada ${fmtFecha(h.fecha_entrada)}`;
                const tituloS = `${tipoLabel} #${h.pipa_numero} — Salida ${fmtFecha(h.fecha_regreso_real)}`;
                return (
                  <div key={h.id} style={{
                    background: "white", borderRadius: 12,
                    border: `1.5px solid ${abierto ? "#FDE68A" : "#E2E8F0"}`,
                    padding: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  }}>
                    {/* Cabecera */}
                    <div style={{ display: "flex", justifyContent: "space-between",
                                  alignItems: "center", marginBottom: 8 }}>
                      <span style={{
                        fontWeight: 800, fontSize: 13,
                        color: abierto ? "#92400E" : "#15803D",
                        display: "flex", alignItems: "center", gap: 6,
                      }}>
                        {abierto
                          ? <><i className="fa-solid fa-screwdriver-wrench"/>EN TALLER</>
                          : <><i className="fa-solid fa-circle-check"/>SALIDA</>}
                      </span>
                      <span style={{ fontSize: 12, color: "#94A3B8" }}>
                        Entrada: {fmtFecha(h.fecha_entrada)}
                        {h.fecha_regreso_real && <> · Salida: {fmtFecha(h.fecha_regreso_real)}</>}
                      </span>
                    </div>

                    {/* Detalles */}
                    {h.motivo && (
                      <div style={{ fontSize: 13, color: "#334155", marginBottom: 4 }}>
                        <strong>Motivo:</strong> {h.motivo}
                      </div>
                    )}
                    {h.lugar_taller && (
                      <div style={{ fontSize: 13, color: "#334155", marginBottom: 4 }}>
                        <strong>Lugar:</strong> {h.lugar_taller}
                      </div>
                    )}
                    {h.registrado_por_nombre && (
                      <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 8 }}>
                        <i className="fa-solid fa-user" style={{ marginRight: 4 }}/>
                        Registró: {h.registrado_por_nombre}
                      </div>
                    )}

                    {/* Fotos de entrada */}
                    {fotosE.length > 0 && (
                      <div style={{ marginBottom: fotosS.length ? 10 : 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#92400E",
                                      marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                          <i className="fa-solid fa-camera" style={{ marginRight: 4 }}/>
                          Fotos de entrada ({fotosE.length})
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {fotosE.map((url, i) => (
                            <FotoThumb key={i} url={url} fotos={fotosE} titulo={tituloE} onOpen={setFotoModal}/>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Fotos de salida */}
                    {fotosS.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#15803D",
                                      marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                          <i className="fa-solid fa-camera" style={{ marginRight: 4 }}/>
                          Fotos de salida ({fotosS.length})
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {fotosS.map((url, i) => (
                            <FotoThumb key={i} url={url} fotos={fotosS} titulo={tituloS} onOpen={setFotoModal}/>
                          ))}
                        </div>
                      </div>
                    )}

                    {fotosE.length === 0 && fotosS.length === 0 && (
                      <div style={{ fontSize: 12, color: "#CBD5E1", fontStyle: "italic" }}>
                        Sin fotos en este registro
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            </>
          )}

          <div style={{ textAlign: "center", marginTop: 24, fontSize: 11, color: "#CBD5E1" }}>
            DMU Chihuahua · Sistema de Control Vehicular
          </div>
        </main>

        {/* ── Modal foto ampliada ── */}
        {fotoModal && (
          <div
            onClick={() => setFotoModal(null)}
            className="no-print"
            style={{
              position: "fixed", inset: 0, zIndex: 9999,
              background: "rgba(0,0,0,0.88)",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <div onClick={(e) => e.stopPropagation()}
                 style={{ maxWidth: "92vw", maxHeight: "92vh", cursor: "default" }}>
              <img
                src={fotoModal.url} alt="evidencia ampliada"
                style={{ maxWidth: "100%", maxHeight: "75vh", borderRadius: 10, display: "block" }}
              />
              <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "center", marginTop: 12, color: "white" }}>
                <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.9 }}>{fotoModal.titulo}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <a href={fotoModal.url}
                     download={`foto_${Date.now()}.jpg`}
                     style={{ background: "rgba(255,255,255,0.2)", color: "white", padding: "5px 14px",
                              borderRadius: 6, textDecoration: "none", fontWeight: 700, fontSize: 12,
                              display: "flex", alignItems: "center", gap: 4 }}
                     onClick={(e) => e.stopPropagation()}>
                    <i className="fa-solid fa-download"/> Descargar
                  </a>
                  <a href={fotoModal.url} target="_blank" rel="noopener noreferrer"
                     style={{ background: "rgba(255,255,255,0.2)", color: "white", padding: "5px 14px",
                              borderRadius: 6, textDecoration: "none", fontWeight: 700, fontSize: 12 }}>
                    <i className="fa-solid fa-up-right-from-square"/> Abrir
                  </a>
                  <button onClick={() => setFotoModal(null)}
                    style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "none",
                             padding: "5px 14px", borderRadius: 6, fontWeight: 700, cursor: "pointer", fontSize: 12 }}>
                    <i className="fa-solid fa-xmark"/> Cerrar
                  </button>
                </div>
              </div>
              {fotoModal.fotos.length > 1 && (
                <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                  {fotoModal.fotos.map((u, i) => (
                    <button key={i} onClick={() => setFotoModal((p) => ({ ...p, url: u }))}
                      style={{ border: "none", background: "none", padding: 0, cursor: "pointer" }}>
                      <img src={u} alt={`f${i}`} style={{
                        width: 56, height: 56, objectFit: "cover", borderRadius: 4,
                        border: u === fotoModal.url ? "2px solid #C9A84C" : "2px solid transparent",
                      }}/>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
